import { Amendment, Member, MemberAddAmendment, MemberAmendment, MemberModifyAmendment, Vehicle, VehicleAddAmendment, VehicleAmendmentBaseFields, VehicleModifyAmendment } from 'types/expiryRenewal';

/**
 * Every guard below narrows on productCode as well as amendmentType. "amendments"
 * carries vehicle and member changes alike, so matching on the type alone counts a
 * member as an extra vehicle - see the note on the Amendment union.
 */
export const isMemberAdd = (a: Amendment): a is MemberAddAmendment => a.amendmentType === 'Add' && a.productCode === 'ExtraPerson';

export const isVehicleAdd = (a: Amendment): a is VehicleAddAmendment => a.amendmentType === 'Add' && a.productCode === 'ExtraVehicle';

export const isVehicleModify = (a: Amendment): a is VehicleModifyAmendment => a.amendmentType === 'Modify' && a.productCode === 'Vehicle';

export const isMemberModify = (a: Amendment): a is MemberModifyAmendment => a.amendmentType === 'Modify' && a.productCode === 'Member';

/**
 * The date input yields a bare "YYYY-MM-DD"; v3/renewal/create wants an ISO 8601
 * instant. Pinned to UTC midnight rather than going through local time, so a member
 * born on the 4th is not posted as the 3rd from a BST or negative-offset browser.
 */
export const toIsoDateOfBirth = (date: string): string => `${date}T00:00:00.000Z`;

/**
 * Renders a staged member add through the same shape the members list already
 * displays, so added members sit alongside the ones the quote came back with.
 * They are never the lead member and always carry a DOB - the form requires it.
 */
export const toMember = ({ memberAdd }: MemberAddAmendment): Member => ({
  leadMember: false,
  hasDateOfBirth: true,
  title: memberAdd.title,
  firstName: memberAdd.firstName,
  surname: memberAdd.surname,
  dateOfBirth: memberAdd.dateOfBirth,
});

/**
 * The three fields the BE matches a member on. Every member lookup on the customise
 * step goes through this rather than an id, because the quote does not carry one.
 */
export type MemberIdentity = {
  firstName: string;
  surname: string;
  dateOfBirth: string;
};

const normalizeMemberName = (name: string) => name.trim().toLowerCase();

/**
 * The date component of a date of birth, as "YYYY-MM-DD" - which is also what a
 * date input wants for its value. Read off the string rather than through Date
 * where possible: the quote can return an instant carrying a time, and parsing
 * "1969-03-18T23:30:00Z" in a positive-offset browser would roll it to the 19th.
 */
export const toDatePart = (value: string): string => {
  if (!value) return '';
  const [iso] = /^\d{4}-\d{2}-\d{2}/.exec(value) ?? [];
  if (iso) return iso;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * Matches the BE rule: names trimmed and case-insensitive, date of birth on its
 * date component only.
 */
export const isSameMemberIdentity = (a: MemberIdentity, b: MemberIdentity): boolean => (
  normalizeMemberName(a.firstName) === normalizeMemberName(b.firstName)
  && normalizeMemberName(a.surname) === normalizeMemberName(b.surname)
  && toDatePart(a.dateOfBirth) === toDatePart(b.dateOfBirth)
);

/**
 * The member a Modify amendment points AT - its "old" triple, never its new details.
 * A member edited twice must keep identifying itself by the details the quote came
 * back with, or the second amendment names a member the quote does not hold.
 */
export const amendedMemberIdentity = ({ memberAmend }: MemberModifyAmendment): MemberIdentity => ({
  firstName: memberAmend.oldFirstName,
  surname: memberAmend.oldSurname,
  dateOfBirth: memberAmend.oldDateOfBirth,
});

/**
 * The staged amendment for a member, whether they came off the quote and were edited
 * or were added during this journey. Modify is matched on its old identity, Add on the
 * details it currently holds - which is what the members list displays for it.
 */
export const findMemberAmendment = (
  member: MemberIdentity,
  amendments: Amendment[],
): MemberAmendment | undefined => amendments.find((a): a is MemberAmendment => (
  (isMemberModify(a) && isSameMemberIdentity(amendedMemberIdentity(a), member))
  || (isMemberAdd(a) && isSameMemberIdentity(a.memberAdd, member))
));

/**
 * Overlays a staged edit onto a member off the quote, so the list shows the new
 * details while the amendment keeps pointing at the original ones. The mirror of
 * applyModifyAmendment for vehicles.
 */
export const applyMemberAmendment = (member: Member, amendments: Amendment[]): Member => {
  const amendment = amendments
    .find((a): a is MemberModifyAmendment => isMemberModify(a) && isSameMemberIdentity(amendedMemberIdentity(a), member));

  if (!amendment) return member;

  return {
    ...member,
    hasDateOfBirth: true,
    title: amendment.memberAmend.title,
    firstName: amendment.memberAmend.firstName,
    surname: amendment.memberAmend.surname,
    dateOfBirth: amendment.memberAmend.dateOfBirth,
  };
};

export const normalizeReg = (reg: string) => reg.replace(/\s/g, '').toUpperCase();

export const applyModifyAmendment = (v: Vehicle, amendments: Amendment[]): Vehicle => {
  const amendment = amendments
    .find((a): a is VehicleModifyAmendment => isVehicleModify(a) && a.vehicleAmend.oldRegistration === v.Registration);

  return amendment ? { ...v, Registration: amendment.vehicleAmend.newRegistration } : v;
};

export const buildTakenRegs = (
  policyVehicles: Vehicle[],
  amendments: Amendment[],
  excludeReg?: string,
): Set<string> => {
  const policyRegs = policyVehicles
    .filter((v) => !excludeReg || normalizeReg(v.Registration) !== normalizeReg(excludeReg))
    .map((v) => normalizeReg(applyModifyAmendment(v, amendments).Registration));

  const addedRegs = amendments
    .filter((a): a is VehicleAddAmendment => isVehicleAdd(a) && (!excludeReg || normalizeReg(a.vehicleAdd.registration) !== normalizeReg(excludeReg)))
    .map((a) => normalizeReg(a.vehicleAdd.registration));

  return new Set([...policyRegs, ...addedRegs]);
};

export const buildVehicleAmendFields = (fields: Vehicle, searchedFuelType: string | null): VehicleAmendmentBaseFields => ({
  make: fields.Make,
  model: fields.Model,
  mileage: 0,
  fuelType: searchedFuelType ?? 'Petrol',
  isDvlaVehicle: false,
  yearOfManufacture: Number(fields.YearOfManufacture) || 0,
  registrationDate: '1900-01-01',
});
